import { ArrowLeftIcon, CheckBadgeIcon, ClockIcon, FireIcon, MegaphoneIcon, RocketLaunchIcon, TrophyIcon } from "@heroicons/react/24/solid";
import { ClockIcon as ClockIconOutline, ClipboardDocumentCheckIcon, FireIcon as FireIconOutline } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router";
import { HeroIcon } from "../types";
import { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, Transition } from "@headlessui/react";
import { motion } from "framer-motion";

type Tier = "bronze" | "silver" | "gold";

type Achievement = {
  title: string;
  description: string;
  icon: HeroIcon;
  isEarned: boolean;
  tier: Tier;
};

export const achievements: Achievement[] = [
  {
    title: "First Step",
    description: "Completed your first assessment",
    icon: RocketLaunchIcon,
    isEarned: true,
    tier: "bronze",
  },
  {
    title: "Early Bird",
    description: "Completed an assessment within 1 hour of it being sent",
    icon: ClockIcon,
    isEarned: true,
    tier: "bronze",
  },
  {
    title: "Assessment Achiever",
    description: "Complete 3 assessments",
    icon: CheckBadgeIcon,
    isEarned: false,
    tier: "bronze",
  },
  {
    title: "Consistency",
    description: "Completed assessments 3 months in a row",
    icon: FireIcon,
    isEarned: false,
    tier: "silver",
  },
  {
    title: "Assessment Advocate",
    description: "Reminded a teammate to complete their assessment",
    icon: MegaphoneIcon,
    isEarned: false,
    tier: "silver",
  },
  {
    title: "Dedication Champion",
    description: "Completed assessments 6 months in a row",
    icon: TrophyIcon,
    isEarned: false,
    tier: "gold",
  },
  {
    title: "Team Player",
    description: "Helped your team reach a 90% response rate or above",
    icon: TrophyIcon,
    isEarned: false,
    tier: "gold",
  },
];

const stats = [
  { name: "Current Streak", stat: "2", icon: FireIconOutline },
  { name: "Total Assessments Taken", stat: "3", icon: ClipboardDocumentCheckIcon },
  { name: "Avg. Time to Respond", stat: "4 hr, 8 min", icon: ClockIconOutline },
];

export function Badge({ achievement, index }: { achievement: Achievement; index: number }) {
  const { title, description, icon: Icon, isEarned, tier } = achievement;
  const [flipped, setFlipped] = useState(false);

  const tierStyles: Record<Tier, string> = {
    bronze: "from-amber-600 to-yellow-400 text-white",
    silver: "from-gray-300 to-gray-400 text-gray-900",
    gold: "from-yellow-300 to-yellow-500 text-yellow-900",
  };

  const iconBg: Record<Tier, string> = {
    bronze: "bg-white text-amber-500",
    silver: "bg-white text-gray-500",
    gold: "bg-white text-yellow-500",
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 1 }}
      className="w-72 h-48"
      onMouseEnter={() => achievement.isEarned && setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
      onTouchStart={() => setFlipped((prev) => !prev)}
    >
      <div
        className="relative w-full h-full cursor-default"
        style={{
          transition: "transform 0.6s",
          transformStyle: "preserve-3d",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Front */}
        <div
          className={`absolute inset-0 flex flex-col items-center justify-center p-4 rounded-2xl shadow-2xl bg-gradient-to-r ${
            achievement.isEarned ? tierStyles[tier] : "outline outline-1 outline-gray-300"
          }`}
          style={{ backfaceVisibility: "hidden" }}
        >
          <div className={`p-3 rounded-full shadow-md mb-3 ${iconBg[tier]}`}>
            <Icon className="h-6 w-6" />
          </div>
          <h3 className="text-lg font-bold mb-1">{title}</h3>
          {!achievement.isEarned && <p className="text-sm text-gray-700 font-semibold text-center">{description}</p>}
        </div>

        {/* Back */}
        <div
          className={`absolute inset-0 flex flex-col justify-center items-center p-4 rounded-2xl shadow-2xl text-center bg-gradient-to-r ${tierStyles[tier]}`}
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <p className="text-md text-white font-semibold text-center">{description}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function Achievements() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const earned = achievements.filter((a) => a.isEarned);
  const unearned = achievements.filter((a) => !a.isEarned);

  return (
    <div className="min-h-screen bg-gray-100 p-8 ">
      <ExplanationModal open={open} setOpen={setOpen} />
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-700 hover:text-black mb-4">
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          <span className="font-medium">Back</span>
        </button>
        <h2 className="text-3xl font-bold mb-6 text-center">Your Achievements</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 sm:gap-y-16 max-w-5xl mx-auto place-items-center">
          {earned.map((achievement, index) => (
            <Badge key={achievement.title} achievement={achievement} index={index} />
          ))}
        </div>
        <div>
          <h2 className="mt-16 text-3xl font-bold mb-6 text-center">Your Stats</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 sm:gap-y-16 max-w-5xl mx-auto place-items-center">
            {stats.map((item) => (
              <div key={item.name} className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 w-64">
                <dt className="truncate text-sm font-medium text-gray-500 flex items-center gap-1">
                  {item.name}
                  {item.icon && <item.icon className="h-6" />}
                </dt>
                <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">{item.stat}</dd>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-24">
          <div className="inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300" />
          </div>
        </div>
        <h2 className="mt-24 text-3xl font-bold mb-6 text-center">Unearned Achievements</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-12 sm:gap-y-16 max-w-5xl mx-auto place-items-center">
          {unearned.map((achievement, index) => (
            <div className="opacity-50">
              <Badge key={achievement.title} achievement={achievement} index={index} />
            </div>
          ))}
        </div>

        <div className="w-full mt-24 flex items-center justify-between cursor-pointer">
          <div onClick={() => setOpen(true)} className="mx-auto inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors group">
            <span className="font-medium">How Are Achievements Tracked?</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExplanationModal({ open, setOpen }: { open: boolean; setOpen: (value: boolean) => void }) {
  const faqs = [
    {
      question: "I thought the assessment was anonymous, how then does it know how many assessments I've taken?",
      answer: "We track whether you've taken an assessment — but not what you answered. Achievements are based only on participation metrics (like frequency).",
    },
    {
      question: "Why track participation at all?",
      answer:
        "Understanding participation helps us ensure we're hearing from enough voices to provide accurate, useful insights to your organization — and recognize your engagement through achievements. It also makes it so that only current employees receive the assessment, and each employee can only take the assessment once.",
    },
  ];

  return (
    <Transition appear show={open} as={Dialog} onClose={setOpen} className="relative z-10">
      <Transition.Child
        as="div"
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-500/75" />
      </Transition.Child>

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center text-center sm:items-center">
          <Transition.Child
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-16 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
              {/* Your modal content here */}
              <div className="bg-white mt-5">
                <div className="mx-auto max-w-2xl lg:text-center">
                  <h2 className="text-base font-semibold text-blue-600">How Achievements Work</h2>
                </div>
                <p className="mt-6 text-lg text-gray-600">
                  Achievements are based only on participation — like how many assessments you've taken or if you've shown consistency. But your actual responses are
                  stored separately, with no trace back to a single individual.
                </p>
                <p className="mt-6 text-lg text-gray-600">
                  Think of it like casting a vote in an election. A record is kept that you voted, but your ballot is sealed — no one can see who or what you voted for.
                  The only thing that is counted is how many people voted.
                </p>
                <div className="mt-10 mx-auto max-w-7xl">
                  <dl className="divide-y divide-gray-900/10">
                    {faqs.map((faq, index) => (
                      <div key={index} className="py-8 lg:grid lg:grid-cols-12 lg:gap-8">
                        <dt className="text-base font-semibold text-gray-900 lg:col-span-5">{faq.question}</dt>
                        <dd className="mt-4 lg:col-span-7 lg:mt-0">
                          <p className="text-base text-gray-600">{faq.answer}</p>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 cursor-pointer">
                <div onClick={() => setOpen(false)} className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors group">
                  <ArrowLeftIcon className="h-4 w-6" />
                  <span className="font-medium">Back</span>
                </div>
              </div>
            </DialogPanel>
          </Transition.Child>
        </div>
      </div>
    </Transition>
  );
}
